import { Typography, Box } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useErrorHandler } from "react-error-boundary";
import InfiniteScroll from "react-infinite-scroll-component";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "underscore";
import {
  Alert,
  DefaultLoader,
  MenuBar,
  SavingDetailCard,
  Transaction,
  TransactionInfiniteLoader,
} from "../../components";
import { icons } from "../../constants";
import withProfileRedirect from "../../containers/App/withProfileRedirect";
import { showError } from "../../globalRedux/actions";
import { transformMiniTransactions } from "../../utils/functions/transformMiniTransactions";
import { useEcddChecking } from "../../utils/hooks";
import { getAccountStatusV2 } from "../dashboard/redux/actions";
import { validateValue } from "../fundDetails/utils";
import { getTransactionHistory } from "../redux/action";

import styles from "./styles";

import { dataLayerGTM } from "../../utils/functions/dataLayerGTM";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { sendFundsCode } from "../fundSelection/redux/action";
import "./styles.scss";
import { RETIRE } from "../../utils/constants";

const liveAgentWhatsappNumber =
    process.env.REACT_APP_LIVE_AGENT_WHATSAPP_NUMBER?.trim();
const liveAgentWhatsappNumberException1 =
    process.env.REACT_APP_LIVE_AGENT_WHATSAPP_NUMBER_EXCEPTION_1?.trim();
const RetirementDetail = props => {
  const {
    history,
    match,
    funds,
    transactionHistory,
    loadingTransactionHistory,
    ecddStatus,
    profile,
    retirementFunds,
    showError,
    sendFundsCode,
    getAccountStatusV2,
    accountStatusLoading,
    getTransactionHistory,
  } = props;

  const errorHandler = useErrorHandler();
  const [retirementWithdrawalAlert, setRetirementWithdrawalAlert] = useState(
    false
  );

  const [retirementDepositAlert, setRetirementDepositAlert] = useState(false);

  const [accountStatus, setAccountStatus] = useState("empty");

  const { handleCheckEcdd, status, onResetStatus } = useEcddChecking({
    ecddStatus,
    aml: profile &&
      profile.amlHistory.length > 0 &&
      typeof profile.amlHistory[0] === "object" && {
        result: profile.amlHistory[0].amlResult,
        status: profile.amlHistory[0].amlSts,
      },
    shouldNotPromptOnEffect: true,
  });

  const [fundDetail] = useState(() => {
    const fund = (retirementFunds || []).find(
      portfolio => portfolio.id === match.params.id
    );

    // in order for react error boundary to catch the error outside the render lifecycle
    // we'll intentionally throw error here in case portfolio is not found
    if (!fund) {
      errorHandler(
        `Cannot find the retirement fund with id of ${match.params.id}`
      );
    }

    return fund;
  });

  const [transactions, setTransactions] = useState([]);

  const pageNumber = useRef(1);
  const transactionsLength = useRef(0);

  const { numPages, totalPages } = transactionHistory;

  const hasCalledAccountStatus = useRef(false);

  const lastTransaction = fundDetail?.lastTransactionSts[0];

  // applicable for customer that do not have prs account yet
  const lastPaymentIsProcessing =
    lastTransaction?.status === "1" && fundDetail?.lastPaymentSts === "00";

  React.useEffect(() => {
    if (hasCalledAccountStatus.current) return;

    if (fundDetail?.investmentPlan?.planId === "P11" && fundDetail?.accountNo) {
      getAccountStatusV2(
        { accountNo: fundDetail.accountNo, params: { type: RETIRE } },
        account => {
          if (account?.sts) {
            setAccountStatus(account.sts);
          }
          hasCalledAccountStatus.current = true;
        }
      );
    } else {
      setAccountStatus("empty");
    }
  }, [fundDetail, hasCalledAccountStatus.current]);

  useEffect(() => {
    getTransactionHistory(
      {
        page: pageNumber.current,
        size: 10,
        cifPlanId: fundDetail.id,
      },
      transactionsResponse => {
        if (!transactionsResponse) return;

        transactionsLength.current = transactionsResponse.length;

        setTransactions(prev => [
          ...transformMiniTransactions(transactionsResponse, prev),
        ]);
      }
    );
  }, []);

  const handleDeposit = () => {
    // Guarranted exist one fund
    const { planAlias, plan, id, product, portfolio } = fundDetail;

    const planNotExistORPlanHasNoPlanFund =
      !plan ||
      (plan instanceof Object &&
        (!plan.hasOwnProperty("planFund") ||
          (plan.hasOwnProperty("planFund") && plan.planFund.length === 0)));

    const planFundHasNoFundCd =
      plan instanceof Object &&
      (plan.hasOwnProperty("planFund") &&
        plan.planFund.length > 0 &&
        plan.planFund[0] instanceof Object &&
        !plan.planFund[0].hasOwnProperty("fundCd"));

    const productNotExistORProductHasNoProductCatCdAndId =
      !product ||
      (product instanceof Object &&
        !product.hasOwnProperty("productCatCd") &&
        !product.hasOwnProperty("id"));

    const planAliasNotExistORPlanAliasHasWrongDataType =
      !planAlias || typeof planAlias !== "string";

    const idNotExistORidHasWrongDataType = !id || typeof id !== "string";

    const isPassedValidation = [
      planNotExistORPlanHasNoPlanFund,
      planFundHasNoFundCd,
      productNotExistORProductHasNoProductCatCdAndId,
      planAliasNotExistORPlanAliasHasWrongDataType,
      idNotExistORidHasWrongDataType,
    ].every(f => f === false);

    if (isPassedValidation) {
      const retireFundPlan = funds.find(fund => fund.id === product.id);
      sendFundsCode([
        {
          name: planAlias,
          //TODO hardcoded string will be change later.
          // conventional = 01
          // islamic = 02
          code: portfolio?.fund?.code
            ? portfolio?.fund?.code
            : product?.id === "MPRetireConventional"
            ? "MYU0100A1PRS"
            : "MYU0100A2PRS",
          id,
          productId: product.id,
          productCatCd: product.productCatCd,
          serviceTaxPerc: retireFundPlan.serviceTaxPerc,
        },
      ]);

      history.push("/retirement/cash-in");
      return;
    }

    const date = new Date();

    showError(
      "We are facing some technical issue, we're sorry for the inconvenience."
    );
    window.log({
      path: window.location.pathname,
      status: "400",
      res: isPassedValidation,
      req: {},
      requestDate: date,
      responseDate: date,
      responseTime: "0ms",
    });
  };

  if (
    accountStatusLoading ||
    (loadingTransactionHistory && pageNumber.current === 1)
  )
    return <DefaultLoader />;

  return (
    <div className="ret-cont">
      <Alert
        title="Account Verification"
        description="Thank you for submitting your information. We need to perform further verification to activate your account."
        isOpen={status === "pending"}
        isCS="https://principal.com.my"
        isCancel={false}
        btnTxt="Chat with us on WhatsApp"
        handleAction={() => {
          onResetStatus();
          window.my.call("openAppLink", {
            url: `https://wa.me/${liveAgentWhatsappNumberException1}`,
          });
        }}
        handleCancel={onResetStatus}
      />
      <Alert
        title="Your account is deactivated"
        description="So sorry, your account is deactivated. We need to perform a verification to activate your account."
        isOpen={status === "rejected"}
        isCS="https://principal.com.my"
        isCancel={false}
        btnTxt="Chat with us on WhatsApp"
        handleAction={() => {
          onResetStatus();
          window.my.call("openAppLink", {
            url: `https://wa.me/${liveAgentWhatsappNumberException1}`,
          });
        }}
        handleCancel={onResetStatus}
      />
      <Alert
        title="Cash In Unsuccessful"
        description={
          lastPaymentIsProcessing
            ? "Your payment is currently in processing. Please check again on your payment status later."
            : "We could not process your request because the retirement account has not been created yet. Please try again later."
        }
        isOpen={retirementDepositAlert}
        isCancel={false}
        btnTxt="Back"
        handleAction={() => {
          setRetirementDepositAlert(false);
        }}
        handleCancel={() => {
          setRetirementDepositAlert(false);
        }}
        withCloseButton
        additionalContent={
          <p
            style={{
              color: "#1278CC",
              fontSize: "16px",
              fontWeight: 400,
              width: "100%",
              textAlign: "center",
              cursor: "pointer",
              display: "inline-block",
              marginBottom: "24px",
            }}
            onClick={() => {
              window.my.call("openAppLink", {
                url: `https://wa.me/${liveAgentWhatsappNumber}`,
              });
            }}
          >
            Chat with Customer Service
          </p>
        }
        submitButtonStyle={{ marginTop: "2rem" }}
      />
      <Alert
        title="Cash Out Request"
        description="Please contact our Customer Care Center for further assistance."
        isOpen={retirementWithdrawalAlert}
        isCancel={false}
        btnTxt="Back"
        handleAction={() => {
          setRetirementWithdrawalAlert(false);
        }}
        handleCancel={() => {
          setRetirementWithdrawalAlert(false);
        }}
        withCloseButton
        additionalContent={
          <p
            style={{
              color: "#1278CC",
              fontSize: "16px",
              fontWeight: 400,
              width: "100%",
              textAlign: "center",
              cursor: "pointer",
              display: "inline-block",
              marginBottom: "24px",
            }}
            onClick={() => {
              window.my.call("openAppLink", {
                url: `https://wa.me/${liveAgentWhatsappNumber}`,
              });
            }}
          >
            Chat with Customer Service
          </p>
        }
        submitButtonStyle={{ marginTop: "2rem" }}
      />
      <div className="ret-cont__detail-info-box">
        <div className="ret-cont__detail-info-box__navigation">
          <ArrowBackOutlinedIcon
            sx={{ cursor: "pointer", color: "#ffffff", fontSize: 24 }}
            onClick={() => history.back()}
          />
          <div
            className="ret-cont__detail-info-box__navigation__fund-info-link"
            onClick={() => {
              dataLayerGTM(
                "click_tag",
                "Click",
                "Link",
                "Fund detail - Fund info"
              );
              window.my.navigateTo({
                url:
                  "/pages/link/link?url=" +
                  encodeURIComponent(fundDetail?.fundInfoUrl),
              });
            }}
          >
            <img src={icons.fundInfo} alt="fund info" />
            <p>Fund Info</p>
          </div>
        </div>

        <h1 className="ret-cont__detail-info-box__ret-fund-name">
          {fundDetail.planAlias}
        </h1>
        <h2 className="ret-cont__detail-info-box__total-asset">
          RM{" "}
          <NumberFormat
            value={fundDetail?.currentValue || 0}
            thousandSeparator
            displayType="text"
            decimalScale={2}
            fixedDecimalScale
          />
        </h2>
        {/* <p className="ret-cont__detail-info-box__total-return">
          Total Returns: +RM 20.34
        </p> */}
        <p className="ret-cont__detail-info-box__last-update">
          Last update:{" "}
          {moment(
            validateValue(fundDetail?.portfolio?.performanceUpdateDate, "DATE")
          )
            .utcOffset("+0800")
            .format("DD MMM YYYY, h:mm A")}
        </p>

        <div className="ret-cont__detail-info-box__grid">
          <SavingDetailCard
            label="Number of Units"
            needNumberFormat={true}
            decimalSet={4}
            detail={validateValue(fundDetail?.portfolio?.unitHeld, "NUMBER")}
          />
        </div>
      </div>

      <Box
        sx={{
          backgroundColor: "#f6f6f8",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          paddingBottom: "8rem",
        }}
      >
        {transactions && transactions.length > 0 && (
          <Typography
            style={{
              ...styles.baseFont,
              padding: "16px 16px 0",
              color: "#616367",
            }}
          >
            You can refer to your Cash In or Cash Out under processing at{" "}
            <span
              style={{ color: "#1278CC" }}
              onClick={() => history.push("/transaction")}
            >
              transaction history
            </span>
            .
          </Typography>
        )}
        {transactions && transactions.length > 0 ? (
          <InfiniteScroll
            dataLength={transactionsLength.current}
            style={{ paddingBottom: "8rem" }}
            next={() => {
              if (window.scrollY === 0) return;

              getTransactionHistory(
                {
                  page: (pageNumber.current += 1),
                  size: 10,

                  cifPlanId: fundDetail.id,
                },
                transactionsResponse => {
                  if (!transactionsResponse) return;

                  transactionsLength.current += transactionsResponse.length;

                  setTransactions(prev => [
                    ...transformMiniTransactions(transactionsResponse, prev),
                  ]);
                }
              );
            }}
            hasMore={numPages < totalPages}
            loader={
              loadingTransactionHistory &&
              pageNumber.current > 1 && (
                <TransactionInfiniteLoader style={{ marginTop: "30px" }} />
              )
            }
          >
            <Transaction transactions={transactions} />
          </InfiniteScroll>
        ) : (
          <Box
            sx={{
              lineHeight: 1.38,
              color: "#616367",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "auto 0",
            }}
          >
            <div>
              You can refer to your Cash In or Cash Out under processing at{" "}
              <span
                style={{ color: "#1278CC" }}
                onClick={() => history.push("/transaction")}
              >
                transaction history
              </span>
              .
              <br />
              Start Cash In now.
            </div>
          </Box>
        )}
      </Box>
      <div className="ret-cont__menu-bar">
        <MenuBar
          productType="retirement"
          handleWithdraw={() => {
            setRetirementWithdrawalAlert(true);
          }}
          handleDeposit={() => {
            const PRSNumberStatusNotActive =
              accountStatus.toLowerCase() !== "active";
            const lastTransaction = retirementFunds[0]?.lastTransactionSts[0];

            // not have prs and last transaction status is 3,4,9 = blocked
            // last transaction status is = 1 and last payment status is 00 = blocked
            // last transaction status other than 6 and 1 is blocked

            if (
              PRSNumberStatusNotActive &&
              lastTransaction &&
              (["3", "4", "9"].includes(lastTransaction?.status) ||
                lastPaymentIsProcessing ||
                (lastTransaction?.status !== "6" &&
                  lastTransaction?.status !== "1"))
            ) {
              return setRetirementDepositAlert(true);
            }

            return handleCheckEcdd({
              cb: handleDeposit,
              ecddExpiredCb: () => history.push("/onBoarding/ecdd/FUNDDETAILS"),
              gtm: {
                event: "click_tag",
                action: "Click",
                category: "Button",
                label: "Retirement Fund detail - Cash In",
              },
            })();
          }}
          isBackgroundGrayed
        />
      </div>
    </div>
  );
};

const withConnect = connect(
  state => ({
    ...state.dashboardReducer,
    funds: state.fundSelectionReducer.fundList,
    loadingTransactionHistory: state.goal.loadingTransactionHistory,
    transactionHistory: state.goal.transactionHistory,
    ecddStatus: state.goal.ecddStatus,
    profile: state.goal.profile,
    retirementFunds: state.goal.retirementFunds,
    accountStatusLoading: state.dashboardReducer.accountStatusLoading,
  }),
  {
    showError,
    sendFundsCode,
    getAccountStatusV2,
    getTransactionHistory,
  }
);

export default compose(
  withRouter,
  withConnect,
  withProfileRedirect
)(RetirementDetail);
